import { ApiClient } from './infrastructure/api/client';
import { PascoRepositoryImpl } from './infrastructure/pasco/pasco_repository_impl';
import { TaskRepositoryImpl } from './infrastructure/task/task_repository_impl';
import { StatsRepositoryImpl } from './infrastructure/stats/stats_repository_impl';
import { HealthRepositoryImpl } from './infrastructure/system/health_repository_impl';
import { AuthRepositoryImpl } from './infrastructure/auth/auth_repository_impl';
import { RoleRepositoryImpl } from './infrastructure/rbac/role_repository_impl';
import { PermissionRepositoryImpl } from './infrastructure/rbac/permission_repository_impl';
import { UserRepositoryImpl } from './infrastructure/users/user_repository_impl';
import { UniversityRepositoryImpl } from './infrastructure/universities/university_repository_impl';
import { CreditBalanceRepositoryImpl } from './infrastructure/credits/credit-balance-repository-impl';
import { GoogleAnalyticsRepositoryImpl } from './infrastructure/analytics/google_analytics_repository_impl';
import type { IPascoRepository } from './domain/pascos/repository/pasco_repository';
import type { ITaskRepository } from './domain/pascos/repository/task_repository';
import type { IStatsRepository } from './domain/stats/repository/stats_repository';
import type { IHealthRepository } from './domain/system/repository/health_repository';
import type { IAuthRepository, IUserRepository } from './domain/users/repository';
import type { IRoleRepository, IPermissionRepository } from './domain/rbac/repository';
import type { UniversityRepository } from './domain/universities/repository/university_repository';
import type { CreditBalanceRepository } from './domain/credits/repository/credit-balance.repository';
import type { IAnalyticsRepository } from './domain/analytics/repository/analytics_repository';
import { PascoService } from './application/pascos/usecases/pasco.service';
import { TaskService } from './application/tasks/usecases/task.service';
import { StatsService } from './application/stats/usecases/stats.service';
import { HealthService } from './application/system/usecases/health.service';
import { AuthService } from './application/users/usecases/auth.service';
import { RbacService } from './application/rbac/usecases/rbac.service';
import { UserService } from './application/users/usecases/user.service';
import { UniversityService } from './application/universities/usecases/university.service';
import { AnalyticsService } from './application/analytics/usecases/analytics.service';
import { ListUniversitiesUseCase } from './application/universities/usecases/list_universities.usecase';
import { CreateUniversityUseCase } from './application/universities/usecases/create_university.usecase';
import { UpdateUniversityUseCase } from './application/universities/usecases/update_university.usecase';
import { GetUniversityByIdUseCase } from './application/universities/usecases/get_university_by_id.usecase';
import { GetPublicPascosUseCase } from './application/pascos/usecases/get_public_pascos.usecase';
import { GetPublicPascoFilterOptionsUseCase } from './application/pascos/usecases/get_public_pasco_filter_options.usecase';
import { SearchPublicPascosUseCase } from './application/pascos/usecases/search_public_pascos.usecase';
import { GetCreditBalanceUseCase } from './application/credits/usecases/get-credit-balance.usecase';

/**
 * Dependency Injection Container
 * Manages all application dependencies and their lifecycles
 */
class Container {
    private static instance: Container;
    private services: Map<string, any> = new Map();

    private constructor() {
        this.registerServices();
    }

    /**
     * Get singleton instance of the container
     */
    public static getInstance(): Container {
        if (!Container.instance) {
            Container.instance = new Container();
        }
        return Container.instance;
    }

    /**
     * Register all application services
     */
    private registerServices(): void {
        // Register API Client
        const apiClient = new ApiClient({
            baseURL: import.meta.env.VITE_API_BASE_URL,
            timeout: 30000,
            withCredentials: false,
        });
        this.register('ApiClient', apiClient);

        // Register Repositories
        const pascoRepository = new PascoRepositoryImpl(apiClient);
        this.register<IPascoRepository>(
            'PascoRepository',
            pascoRepository
        );

        const taskRepository = new TaskRepositoryImpl(apiClient);
        this.register<ITaskRepository>(
            'TaskRepository',
            taskRepository
        );

        const statsRepository = new StatsRepositoryImpl(apiClient);
        this.register<IStatsRepository>(
            'StatsRepository',
            statsRepository
        );

        const healthRepository = new HealthRepositoryImpl(apiClient);
        this.register<IHealthRepository>(
            'HealthRepository',
            healthRepository
        );

        const authRepository = new AuthRepositoryImpl(apiClient);
        this.register<IAuthRepository>(
            'AuthRepository',
            authRepository
        );

        const roleRepository = new RoleRepositoryImpl(apiClient);
        this.register<IRoleRepository>(
            'RoleRepository',
            roleRepository
        );

        const permissionRepository = new PermissionRepositoryImpl(apiClient);
        this.register<IPermissionRepository>(
            'PermissionRepository',
            permissionRepository
        );

        const userRepository = new UserRepositoryImpl(apiClient);
        this.register<IUserRepository>(
            'UserRepository',
            userRepository
        );

        const universityRepository = new UniversityRepositoryImpl(apiClient);
        this.register<UniversityRepository>(
            'UniversityRepository',
            universityRepository
        );

        const creditBalanceRepository = new CreditBalanceRepositoryImpl();
        this.register<CreditBalanceRepository>(
            'CreditBalanceRepository',
            creditBalanceRepository
        );

        const analyticsRepository = new GoogleAnalyticsRepositoryImpl();
        this.register<IAnalyticsRepository>(
            'AnalyticsRepository',
            analyticsRepository
        );

        // Register Use Cases / Services
        this.register<PascoService>(
            'PascoService',
            new PascoService(pascoRepository)
        );

        this.register<GetPublicPascosUseCase>(
            'GetPublicPascosUseCase',
            new GetPublicPascosUseCase(pascoRepository)
        );

        this.register<GetPublicPascoFilterOptionsUseCase>(
            'GetPublicPascoFilterOptionsUseCase',
            new GetPublicPascoFilterOptionsUseCase(pascoRepository)
        );

        this.register<SearchPublicPascosUseCase>(
            'SearchPublicPascosUseCase',
            new SearchPublicPascosUseCase(pascoRepository)
        );

        this.register<TaskService>(
            'TaskService',
            new TaskService(taskRepository)
        );

        this.register<StatsService>(
            'StatsService',
            new StatsService(statsRepository)
        );

        this.register<HealthService>(
            'HealthService',
            new HealthService(healthRepository)
        );

        this.register<AuthService>(
            'authService',
            new AuthService(authRepository)
        );

        this.register<RbacService>(
            'RbacService',
            new RbacService(roleRepository, permissionRepository)
        );

        this.register<UserService>(
            'UserService',
            new UserService(userRepository)
        );

        this.register<UniversityService>(
            'UniversityService',
            new UniversityService(
                new ListUniversitiesUseCase(universityRepository),
                new CreateUniversityUseCase(universityRepository),
                new UpdateUniversityUseCase(universityRepository),
                new GetUniversityByIdUseCase(universityRepository)
            )
        );

        this.register<GetCreditBalanceUseCase>(
            'GetCreditBalanceUseCase',
            new GetCreditBalanceUseCase(creditBalanceRepository)
        );

        this.register<AnalyticsService>(
            'AnalyticsService',
            new AnalyticsService(analyticsRepository)
        );
    }

    /**
     * Register a service in the container
     */
    public register<T>(key: string, service: T): void {
        this.services.set(key, service);
    }

    /**
     * Resolve a service from the container
     */
    public resolve<T>(key: string): T {
        const service = this.services.get(key);
        if (!service) {
            throw new Error(`Service ${key} not found in container`);
        }
        return service as T;
    }

    /**
     * Check if a service exists in the container
     */
    public has(key: string): boolean {
        return this.services.has(key);
    }

    /**
     * Clear all services (useful for testing)
     */
    public clear(): void {
        this.services.clear();
    }
}

// Export singleton instance
export const container = Container.getInstance();

// Export typed service accessors for convenience
export const getApiClient = (): ApiClient => container.resolve<ApiClient>('ApiClient');
export const getPascoRepository = (): IPascoRepository => container.resolve<IPascoRepository>('PascoRepository');
export const getTaskRepository = (): ITaskRepository => container.resolve<ITaskRepository>('TaskRepository');
export const getStatsRepository = (): IStatsRepository => container.resolve<IStatsRepository>('StatsRepository');
export const getHealthRepository = (): IHealthRepository => container.resolve<IHealthRepository>('HealthRepository');
export const getAuthRepository = (): IAuthRepository => container.resolve<IAuthRepository>('AuthRepository');
export const getRoleRepository = (): IRoleRepository => container.resolve<IRoleRepository>('RoleRepository');
export const getPermissionRepository = (): IPermissionRepository => container.resolve<IPermissionRepository>('PermissionRepository');
export const getUserRepository = (): IUserRepository => container.resolve<IUserRepository>('UserRepository');

// Export use case service accessors
export const getPascoService = (): PascoService => container.resolve<PascoService>('PascoService');
export const getTaskService = (): TaskService => container.resolve<TaskService>('TaskService');
export const getStatsService = (): StatsService => container.resolve<StatsService>('StatsService');
export const getHealthService = (): HealthService => container.resolve<HealthService>('HealthService');
export const getAuthService = (): AuthService => container.resolve<AuthService>('authService');
export const getRbacService = (): RbacService => container.resolve<RbacService>('RbacService');
export const getUserService = (): UserService => container.resolve<UserService>('UserService');
export const getCreditBalanceUseCase = (): GetCreditBalanceUseCase => container.resolve<GetCreditBalanceUseCase>('GetCreditBalanceUseCase');
export const getAnalyticsService = (): AnalyticsService => container.resolve<AnalyticsService>('AnalyticsService');

